import { useState, useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  confirmLabel?: ReactNode;
  cancelLabel?: ReactNode;
  variant?: 'default' | 'destructive';
  onConfirm: () => void | Promise<void>;
  /** When set, renders a typed-phrase input and keeps the confirm button
   *  disabled until the user types the exact value. Existing callers that
   *  omit this prop behave exactly as before (button always enabled). */
  requiredPhrase?: string;
  /** Optional label rendered above the phrase input. */
  phraseLabel?: ReactNode;
  /** Optional placeholder for the phrase input. */
  phrasePlaceholder?: string;
}

/**
 * Two-button confirmation modal. Default variant is destructive (delete
 * confirmation is the dominant use case in F017+). Per docs/07 §21.5,
 * destructive actions must require an explicit confirmation step.
 *
 * When `requiredPhrase` is provided, an additional text input is rendered
 * and the confirm button stays disabled until the typed value exactly matches
 * the phrase (used by the F047 account-deletion Zona de perigo).
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel,
  variant = 'destructive',
  onConfirm,
  requiredPhrase,
  phraseLabel,
  phrasePlaceholder,
}: ConfirmDialogProps) {
  const { t } = useTranslation();
  const resolvedConfirmLabel = confirmLabel ?? t('common.confirm');
  const resolvedCancelLabel = cancelLabel ?? t('common.cancel');
  const [typedPhrase, setTypedPhrase] = useState('');

  // Reset the typed phrase whenever the dialog opens or closes so a
  // re-opened dialog always starts empty.
  useEffect(() => {
    if (!open) {
      setTypedPhrase('');
    }
  }, [open]);

  const confirmDisabled = requiredPhrase !== undefined ? typedPhrase !== requiredPhrase : false;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
        </DialogHeader>

        {requiredPhrase !== undefined && (
          <div className="space-y-1.5 py-2">
            {phraseLabel ? <p className="text-sm font-medium">{phraseLabel}</p> : null}
            <Input
              data-testid="delete-phrase-input"
              type="text"
              value={typedPhrase}
              onChange={(e) => setTypedPhrase(e.target.value)}
              placeholder={phrasePlaceholder}
              autoComplete="off"
            />
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {resolvedCancelLabel}
          </Button>
          <Button
            type="button"
            data-testid="confirm-delete"
            variant={variant}
            disabled={confirmDisabled}
            onClick={async () => {
              await onConfirm();
              onOpenChange(false);
            }}
          >
            {resolvedConfirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
